<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  BuildingOffice2Icon,
  CalendarIcon,
  UserIcon,
} from '@heroicons/vue/24/outline'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import EligibilityDetails from '@/components/features/eligibility/EligibilityDetails.vue'
import { useEligibilityStore } from '@/stores/useEligibilityStore'

const route = useRoute()
const eligibilityStore = useEligibilityStore()
const $toast = useToast()

const eligibility = ref<any>(null)
const notFound = ref(false)

const eligibilityUuid = computed(() => String(route.params.eligibilityUuid ?? ''))

async function load() {
  if (!eligibilityUuid.value) return
  notFound.value = false
  eligibility.value = null
  const data = await eligibilityStore.fetchEligibilityDetails(eligibilityUuid.value)
  if (data) {
    eligibility.value = data
  } else {
    notFound.value = true
  }
}

onMounted(load)
watch(eligibilityUuid, load)

const authorName = computed(() => {
  const author = eligibility.value?.author
  if (!author) return null
  return [author.firstName, author.lastName].filter(Boolean).join(' ') || author.email
})

const company = computed(() => eligibility.value?.company ?? null)

function formatDate(date?: string) {
  if (!date) return '—'
  return new Date(date).toLocaleString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

async function exportCsv() {
  if (!eligibility.value?.uuid) return
  try {
    // Mêmes filtres par défaut que l'export depuis la modal de l'historique
    await eligibilityStore.exportToCsv(eligibility.value.uuid, {
      commitmentMonths: 36,
      technology: 'FTTH',
      bandwidthMbps: 1000,
    })
  } catch (error) {
    $toast.error(error instanceof Error ? error.message : "Erreur lors de l'export")
  }
}
</script>

<template>
  <div class="p-6">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <Bs2Title
        title="Détail de l'éligibilité All-In-One"
        :subtitle="eligibility?.fulladdress ?? ''"
      />
      <div class="flex gap-2">
        <router-link to="/eligibility" class="btn btn-sm btn-ghost gap-1">
          <ArrowLeftIcon class="w-4 h-4" />
          Historique
        </router-link>
        <button v-if="eligibility" class="btn btn-sm btn-outline gap-1" @click="exportCsv">
          <ArrowDownTrayIcon class="w-4 h-4" />
          CSV
        </button>
      </div>
    </div>

    <!-- Carte récapitulative -->
    <div v-if="eligibility" class="mt-4 card bg-base-100 shadow">
      <div class="card-body py-4 flex flex-row flex-wrap items-center gap-x-8 gap-y-2 text-sm">
        <div v-if="company" class="flex items-center gap-2">
          <BuildingOffice2Icon class="w-4 h-4 text-base-content/50 flex-shrink-0" />
          <router-link
            v-if="company.referenceExterne"
            :to="`/partner/${company.referenceExterne}`"
            class="link link-primary font-medium"
          >
            {{ company.name }}
          </router-link>
          <span v-else class="font-medium">{{ company.name }}</span>
          <span v-if="company.siret" class="text-base-content/50">SIRET {{ company.siret }}</span>
        </div>

        <div v-if="authorName" class="flex items-center gap-2">
          <UserIcon class="w-4 h-4 text-base-content/50 flex-shrink-0" />
          <span>Demandée par <span class="font-medium">{{ authorName }}</span></span>
        </div>

        <div class="flex items-center gap-2">
          <CalendarIcon class="w-4 h-4 text-base-content/50 flex-shrink-0" />
          <span>{{ formatDate(eligibility.dateCreated) }}</span>
        </div>

        <div v-if="eligibility.codeBatiment" class="text-base-content/60">
          Code bât. : <span class="font-mono">{{ eligibility.codeBatiment }}</span>
        </div>
      </div>
    </div>

    <!-- Détails (mêmes props que la modal de l'historique) -->
    <div class="mt-4">
      <EligibilityDetails
        v-if="eligibility"
        :result="eligibility.result"
        :fulladdress="eligibility.fulladdress"
        :company-name="company?.name"
        :company-siret="company?.siret"
        :show-filters="true"
      />

      <div v-else-if="notFound" class="card bg-base-100 shadow">
        <div class="card-body items-center text-center py-12 text-base-content/60">
          <p class="text-lg font-medium">Éligibilité introuvable</p>
          <p class="text-sm">Le lien est peut-être invalide ou la demande a été supprimée.</p>
          <router-link to="/eligibility" class="btn btn-sm btn-primary mt-2">
            Retour aux éligibilités
          </router-link>
        </div>
      </div>

      <div v-else class="flex flex-col items-center justify-center p-12 gap-4 text-base-content/60">
        <span class="loading loading-spinner loading-lg"></span>
        <p>Chargement de l'éligibilité…</p>
      </div>
    </div>
  </div>
</template>
